import { useMemo, useState } from "react";
import {
  CadenceLookbackWeeks,
  useTimeTrackerStore,
} from "../store/timeTrackerStore";
import { closeGaps, DayInterval, entriesByLocalDay } from "../utils/rhythm";
import {
  buildWeekStats,
  CadencePeriod,
  formatWeekRange,
  MONTHS,
  periodStats,
  typicalWeekStats,
  WeekStat,
} from "../utils/cadence";
import { Button, Card } from "@lazar/ui";

const HOUR_MS = 60 * 60 * 1000;

// Lookback presets for the typical-week window. Mirror of the store union;
// kept here so the chip order is explicit.
const LOOKBACK_OPTIONS: readonly CadenceLookbackWeeks[] = [4, 8, 12, 26, 52];

const PERIOD_OPTIONS: readonly { id: CadencePeriod; label: string }[] = [
  { id: "month", label: "Month" },
  { id: "quarter", label: "Quarter" },
  { id: "year", label: "Year" },
];

// The strip and the typical line are scaled to a 7-day week so a 6- or 7-day
// worker still has headroom and the bars stay comparable across lookbacks.
const WEEK_SCALE_DAYS = 7;

// Render a worked-day count: whole when integer, one decimal otherwise (half
// days produce x.5). Avoids "4.0 days" while keeping "4.5 days".
const fmtDays = (days: number): string =>
  days % 1 === 0 ? `${days}` : days.toFixed(1);

const fmtHours = (ms: number): string => `${Math.round(ms / HOUR_MS)}h`;

// "Jul 3" — the window-start caption. Year is omitted; the caption only ever
// names a day inside the current period, so the year is unambiguous.
const fmtShortDate = (ms: number): string => {
  const d = new Date(ms);
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
};

interface WeekStripProps {
  weeks: WeekStat[];
  medianDays: number;
  now: number;
}

// Bar-track height (px) and the month-label row beneath it. Fixed in px so the
// median reference line can sit at an exact bar-fraction regardless of how the
// flex track resolves.
const TRACK_H = 112;
const LABEL_H = 18;

const weekReadout = (w: WeekStat, now: number): string =>
  `${formatWeekRange(w.weekStart, now)} · ${fmtDays(w.workedDays)} ${
    w.workedDays === 1 ? "day" : "days"
  } · ${fmtHours(w.workMs)}`;

// Per-week bars, oldest → newest left → right, each a focusable button. Hover,
// focus, or tap a week to drive the shared readout (the line above the strip).
// Bars carry a border + a baseline minimum so a zero/light week reads as "0
// days" rather than a gap; the current week is dashed and the inspected week is
// ringed — encodings that don't rely on color alone. A dashed line marks the
// typical (median) days/week. Bars are flex-1 with a min-width: they stretch to
// fill the strip when the lookback fits, and only once they'd shrink past the
// min-width (a long 52-week lookback) does the strip scroll horizontally,
// keeping real hit targets instead of slivers.
const WeekStrip = ({ weeks, medianDays, now }: WeekStripProps) => {
  const ordered = [...weeks].reverse(); // newest is last in buildWeekStats
  const [activeWeek, setActiveWeek] = useState<number | null>(null);
  const fallback = ordered.find((w) => w.isCurrent) ?? ordered[ordered.length - 1];
  const active =
    ordered.find((w) => w.weekStart === activeWeek) ?? fallback;
  const medianLineBottom = LABEL_H + (medianDays / WEEK_SCALE_DAYS) * TRACK_H;
  return (
    <div className="mt-2">
      <div className="flex items-baseline justify-between mb-1 gap-2">
        <span className="text-[10px] font-black uppercase tracking-wider text-ink-soft dark:text-cream/60 shrink-0">
          Days worked per week
        </span>
        <span
          aria-live="polite"
          className="text-[10px] font-mono text-ink dark:text-cream truncate text-right"
        >
          {active ? weekReadout(active, now) : "Hover a week"}
        </span>
      </div>
      <div
        className="relative border-2 border-ink dark:border-cream bg-lemon-50 dark:bg-charcoal rounded-sm overflow-x-auto overflow-y-hidden"
        style={{ height: TRACK_H + LABEL_H }}
        onMouseLeave={() => setActiveWeek(null)}
      >
        {medianDays > 0 && (
          <div
            className="absolute left-0 right-0 border-t-2 border-dashed border-ink/50 dark:border-cream/50 pointer-events-none z-10"
            style={{ bottom: medianLineBottom }}
            aria-hidden="true"
          />
        )}
        <div className="h-full flex items-end gap-[3px] px-1 w-full">
          {ordered.map((w, i) => {
            const heightPx = (w.workedDays / WEEK_SCALE_DAYS) * TRACK_H;
            const fill =
              w.status === "active"
                ? "bg-sky-brut"
                : "bg-ink/20 dark:bg-cream/20";
            const isActive = active?.weekStart === w.weekStart;
            const month = new Date(w.weekStart).getMonth();
            const prevMonth =
              i > 0 ? new Date(ordered[i - 1].weekStart).getMonth() : -1;
            const showMonth = month !== prevMonth;
            return (
              <button
                key={w.weekStart}
                type="button"
                onMouseEnter={() => setActiveWeek(w.weekStart)}
                onFocus={() => setActiveWeek(w.weekStart)}
                onClick={() => setActiveWeek(w.weekStart)}
                aria-label={weekReadout(w, now)}
                className={`flex-1 min-w-[14px] h-full flex flex-col justify-end rounded-sm ${
                  isActive ? "ring-2 ring-ink dark:ring-cream" : ""
                }`}
              >
                <div className="flex-1 min-h-0 flex items-end">
                  <div
                    className={`w-full rounded-sm border border-ink/60 dark:border-cream/60 ${fill} ${
                      w.isCurrent ? "border-dashed" : ""
                    }`}
                    style={{ height: Math.max(heightPx, w.workedDays > 0 ? 4 : 2) }}
                  />
                </div>
                <div
                  className="text-[8px] font-mono leading-none text-ink-soft dark:text-cream/50 text-left whitespace-nowrap flex items-center pointer-events-none"
                  style={{ height: LABEL_H }}
                >
                  {showMonth ? MONTHS[month] : ""}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const WorkCadencePanel = () => {
  // Capture `now` once per mount, like WorkRhythmPanel — the analytics modal
  // remounts this body per open, so "this week / this month" stay anchored to
  // the moment the user opened it. A lazy useState initializer keeps Date.now()
  // out of render (react-hooks/purity); the value is stable for the mount.
  const [now] = useState(() => Date.now());
  const { timeEntries, settings, setCadenceLookbackWeeks } =
    useTimeTrackerStore();
  const allEntries = useMemo(() => Object.values(timeEntries), [timeEntries]);
  const lookback = settings.cadenceLookbackWeeks;
  const { dayHours, workdaysPerWeek } = settings;

  const [period, setPeriod] = useState<CadencePeriod>("month");

  // Raw per-logical-day split drives the worked-day weights (a binary-ish
  // classification barely moved by smoothing). A reporting-smoothed copy drives
  // the hours figures so they reconcile with billing and the Rhythm cards.
  const byDayRaw = useMemo(
    () => entriesByLocalDay(allEntries, now),
    [allEntries, now],
  );
  const reportingSmoothingMs = settings.smoothing.enabled
    ? settings.smoothing.thresholdMinutes * 60_000
    : 0;
  const byDayHours = useMemo(() => {
    if (reportingSmoothingMs <= 0) return byDayRaw;
    const out = new Map<number, DayInterval[]>();
    for (const [day, ivs] of byDayRaw) {
      out.set(day, closeGaps(ivs, reportingSmoothingMs));
    }
    return out;
  }, [byDayRaw, reportingSmoothingMs]);

  const weeks = useMemo(
    () => buildWeekStats(byDayRaw, byDayHours, now, lookback, dayHours),
    [byDayRaw, byDayHours, now, lookback, dayHours],
  );
  const typical = useMemo(() => typicalWeekStats(weeks), [weeks]);
  const stats = useMemo(
    () =>
      periodStats(byDayRaw, byDayHours, now, period, workdaysPerWeek, dayHours),
    [byDayRaw, byDayHours, now, period, workdaysPerWeek, dayHours],
  );

  if (allEntries.length === 0) {
    return (
      <Card className="p-6">
        <p className="text-ink-soft dark:text-cream/70">
          Start tracking time to see your work weeks.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-6 flex flex-col h-full min-h-0">
      <h2 className="text-lg font-black dark:text-cream mb-1 shrink-0">
        How do my weeks look?
      </h2>
      <p className="text-xs text-ink-soft dark:text-cream/60 mb-4 shrink-0">
        Your week-over-week cadence. A day counts as worked once it passes a
        quarter of your day length (a short day counts as half). Weeks under 3.5
        worked days are treated as time off and left out of the typical figure,
        but still shown on the strip. "Days off" is inferred from days with no
        tracked work, so it assumes you track every day you work.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 shrink-0">
        {/* Typical work week */}
        <Card sub className="p-3">
          <div className="flex items-baseline justify-between gap-2 mb-1 flex-wrap">
            <div className="text-[10px] font-black uppercase tracking-wider text-ink-soft dark:text-cream/60">
              A typical work week{" "}
              <span className="font-mono normal-case font-bold">
                ({typical.activeCount} of last {typical.elapsedCount} weeks)
              </span>
            </div>
            <div
              role="group"
              aria-label="Typical-week lookback window"
              className="flex items-center gap-1"
            >
              {LOOKBACK_OPTIONS.map((option) => {
                const active = option === lookback;
                return (
                  <Button
                    key={option}
                    type="button"
                    onClick={() => setCadenceLookbackWeeks(option)}
                    aria-pressed={active}
                    aria-label={`Lookback: last ${option} weeks`}
                    variant={active ? "primary" : "default"}
                    className="px-2 py-0.5 text-[10px]"
                  >
                    {option}w
                  </Button>
                );
              })}
            </div>
          </div>
          {typical.activeCount === 0 ? (
            <div className="text-sm font-bold text-ink-soft dark:text-cream/70">
              Keep tracking — a typical week needs a few full weeks of work.
            </div>
          ) : (
            <div className="space-y-0.5 text-sm">
              <div className="font-black text-ink dark:text-cream">
                ~{fmtHours(typical.medianHours * HOUR_MS)} per week
              </div>
              <div className="font-mono text-ink-soft dark:text-cream/70 text-xs">
                across ~{fmtDays(typical.medianDays)} days
              </div>
            </div>
          )}
        </Card>

        {/* Period totals + inferred time off */}
        <Card sub className="p-3">
          <div className="flex items-baseline justify-between gap-2 mb-1 flex-wrap">
            <div className="text-[10px] font-black uppercase tracking-wider text-ink-soft dark:text-cream/60">
              This {period}
            </div>
            <div
              role="group"
              aria-label="Time-off period"
              className="flex items-center gap-1"
            >
              {PERIOD_OPTIONS.map((opt) => {
                const active = opt.id === period;
                return (
                  <Button
                    key={opt.id}
                    type="button"
                    onClick={() => setPeriod(opt.id)}
                    aria-pressed={active}
                    aria-label={`Period: this ${opt.id}`}
                    variant={active ? "primary" : "default"}
                    className="px-2 py-0.5 text-[10px]"
                  >
                    {opt.label}
                  </Button>
                );
              })}
            </div>
          </div>
          {stats.shortHistory ? (
            <div className="space-y-0.5 text-sm">
              <div className="font-black text-ink dark:text-cream">
                {fmtHours(stats.workMs)} worked
              </div>
              <div className="font-mono text-ink-soft dark:text-cream/70 text-xs">
                tracking since {fmtShortDate(stats.windowStart)} — not enough
                history yet
              </div>
            </div>
          ) : (
            <div className="space-y-0.5 text-sm">
              <div className="font-black text-ink dark:text-cream">
                {fmtHours(stats.workMs)} worked across{" "}
                {fmtDays(stats.workedDays)} days
              </div>
              <div className="font-mono text-ink-soft dark:text-cream/70 text-xs">
                ~{fmtDays(stats.daysOff)} days off (of {stats.expectedDays}{" "}
                planned)
              </div>
              {stats.clamped && (
                <div className="font-mono text-ink-soft dark:text-cream/50 text-[10px]">
                  since {fmtShortDate(stats.windowStart)}
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* The strip owns its own horizontal scroll, so this wrapper only manages
          vertical space — a second overflow-x here would double-scroll. */}
      <div className="flex-1 min-h-0 overflow-y-hidden">
        <WeekStrip weeks={weeks} medianDays={typical.medianDays} now={now} />
      </div>
    </Card>
  );
};

export default WorkCadencePanel;
